import type { OutlineSection, SourceItem } from '@/types/api';

/**
 * The source picker works the same for every kind of file; only the unit
 * differs. Pages and slides are chosen as parts or typed ranges ("45-80,
 * 120-130"); spreadsheets are chosen by sheet. The server re-checks every
 * selection - these helpers only stop the student sending one it would refuse.
 */
export type ScopeUnit = 'page' | 'slide' | 'cell' | 'minute';

/** Reducto bills one page per this many filled spreadsheet cells. */
export const CELLS_PER_PAGE = 1000;

export interface PageRange {
  start: number;
  end: number;
}

export function scopeUnitOf(source: SourceItem): ScopeUnit {
  const unit = source.size_unit ?? source.outline?.unit;
  return unit === 'slide' || unit === 'cell' || unit === 'minute'
    ? unit
    : 'page';
}

const NOUNS: Record<ScopeUnit, [string, string]> = {
  page: ['page', 'pages'],
  slide: ['slide', 'slides'],
  cell: ['filled cell', 'filled cells'],
  minute: ['minute', 'minutes'],
};

/** "1 page", "12 slides", "15,000 filled cells". */
export function unitCount(unit: ScopeUnit, n: number): string {
  const [one, many] = NOUNS[unit];
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/** The plural noun alone, for "type the pages to use". */
export function unitNoun(unit: ScopeUnit): string {
  return NOUNS[unit][1];
}

/** Where a part sits in the file: "pp. 1–30", "slides 11–20"; sheets have none. */
export function partPosition(unit: ScopeUnit, s: OutlineSection): string {
  if (unit === 'cell') return '';
  if (unit === 'minute') return `${clock(s.start - 1)}–${clock(s.end)}`;
  const prefix = unit === 'slide' ? 'slides' : 'pp.';
  return s.start === s.end
    ? `${prefix} ${s.start}`
    : `${prefix} ${s.start}–${s.end}`;
}

/** Minutes into a video as h:mm:00 / m:00 ("1:05:00", "45:00"). */
function clock(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:00` : `${m}:00`;
}

/** The per-file limit in the source's own unit. */
export function fileLimit(source: SourceItem, unit: ScopeUnit): number | null {
  if (unit === 'cell') return source.max_cells ?? null;
  if (unit === 'minute') return source.max_minutes ?? null;
  return source.max_pages ?? null;
}

/**
 * A video's pages per minute: its transcript counts as pages (1,000 words a
 * page, counted by the server once it has the transcript), so a range of
 * minutes costs its share. Null until the server has counted.
 */
export function pagesPerMinute(source: SourceItem): number | null {
  const minutes = source.size_units ?? source.outline?.total_units ?? 0;
  const pages = source.page_count ?? 0;
  return minutes > 0 && pages > 0 ? pages / minutes : null;
}

/** Pages a selection of `units` is expected to cost (the monthly allowance).
 *  `perMinute` is the video's pages per minute (`pagesPerMinute`). */
export function estimatedPages(
  unit: ScopeUnit,
  units: number,
  perMinute: number | null = null
): number {
  if (unit === 'minute') {
    return units > 0 && perMinute
      ? Math.max(1, Math.ceil(units * perMinute))
      : 0;
  }
  return unit === 'cell'
    ? Math.max(units > 0 ? 1 : 0, Math.ceil(units / CELLS_PER_PAGE))
    : units;
}

export interface ParsedRanges {
  ranges: PageRange[];
  units: number;
  error: string | null;
}

/**
 * Parse typed ranges such as "1-30, 45, 60–80". Overlaps are merged so the
 * count is what will actually be parsed. `total` bounds the ranges when the
 * file's size is known.
 */
export function parseRanges(text: string, total: number | null): ParsedRanges {
  const parts = text
    .split(/[,;\s]+/)
    .map(p => p.trim())
    .filter(Boolean);
  const raw: PageRange[] = [];
  for (const part of parts) {
    const m = /^(\d+)(?:\s*[-–]\s*(\d+))?$/.exec(part);
    if (!m) return { ranges: [], units: 0, error: `"${part}" isn't a range` };
    const start = Number(m[1]);
    const end = m[2] ? Number(m[2]) : start;
    if (start < 1 || end < start) {
      return {
        ranges: [],
        units: 0,
        error: `${part} should go from low to high`,
      };
    }
    if (total && end > total) {
      return { ranges: [], units: 0, error: `This file ends at ${total}` };
    }
    raw.push({ start, end });
  }
  const ranges = mergeRanges(raw);
  const units = ranges.reduce((n, r) => n + r.end - r.start + 1, 0);
  return { ranges, units, error: null };
}

function mergeRanges(ranges: PageRange[]): PageRange[] {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const out: PageRange[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r.start <= last.end + 1) last.end = Math.max(last.end, r.end);
    else out.push({ ...r });
  }
  return out;
}

/**
 * Start with the parts that fit: in order, skipping front/back matter, until
 * the next part would go over `budget` (the smaller of the file limit and the
 * pages left this month, in the source's unit). The student can change it
 * before building.
 */
export function defaultSelection(
  sections: OutlineSection[],
  budget: number | null
): Set<string> {
  const picked = new Set<string>();
  let used = 0;
  for (const s of sections) {
    if (s.is_matter) continue;
    if (budget !== null && used + s.est_units > budget) break;
    picked.add(s.id);
    used += s.est_units;
  }
  return picked;
}

/** The selection budget in the source's unit: the file limit, and for pages
 *  and slides also what is left of the month's allowance. */
export function selectionBudget(
  unit: ScopeUnit,
  limit: number | null,
  pagesLeft: number | null,
  perMinute: number | null = null
): number | null {
  const monthly =
    pagesLeft === null
      ? null
      : unit === 'cell'
        ? pagesLeft * CELLS_PER_PAGE
        : unit === 'minute'
          ? perMinute
            ? Math.floor(pagesLeft / perMinute)
            : null
          : pagesLeft;
  if (limit === null) return monthly;
  if (monthly === null) return limit;
  return Math.min(limit, monthly);
}

/**
 * A sheet narrowed to a row range ("1-2000"): the cells it keeps, estimated in
 * proportion to the rows kept. An empty box means the whole sheet.
 */
export function sheetRowsSelection(
  section: OutlineSection,
  text: string
): { range: PageRange | null; cells: number; error: string | null } {
  if (!text.trim())
    return { range: null, cells: section.est_units, error: null };
  const parsed = parseRanges(text, section.rows || null);
  if (parsed.error) return { range: null, cells: 0, error: parsed.error };
  if (parsed.ranges.length !== 1) {
    return { range: null, cells: 0, error: 'Use one row range, e.g. 1-2000' };
  }
  const range = parsed.ranges[0];
  const kept = range.end - range.start + 1;
  const cells = section.rows
    ? Math.ceil((section.est_units * kept) / section.rows)
    : section.est_units;
  return { range, cells, error: null };
}
